// src/pages/FAQPage.jsx
import { useState } from "react";
import { Link } from "react-router-dom";
import "./FAQPage.css";

const FAQS = [
  {
    category: "Ordering",
    items: [
      {
        q: "How do I place an order?",
        a: "Browse our products, select your specifications (size, quantity, finish), and add to cart. You'll need to create an account or sign in before checkout. Once payment is confirmed, our team will review your artwork and begin printing."
      },
      {
        q: "Can I order a sample before placing a bulk order?",
        a: "Yes! We offer sample packs for most products. Contact us via the Contact page or live chat and we'll arrange a sample for you before you commit to a full order."
      },
      {
        q: "What is the minimum order quantity?",
        a: "Minimum quantities vary by product. Business cards start from 50, flyers from 100, and banners from 1. Each product page shows the available quantities."
      },
      {
        q: "Can I get a custom quote for large orders?",
        a: "Absolutely. For orders over £500 or large volume print runs, contact us directly for a custom quote. We offer significant discounts for bulk orders."
      },
    ]
  },
  {
    category: "Artwork & Files",
    items: [
      {
        q: "What file formats do you accept?",
        a: "We accept PDF, AI (Adobe Illustrator), EPS, PSD (Photoshop), and high-resolution TIFF files. PDF is our preferred format as it preserves fonts and layouts perfectly."
      },
      {
        q: "What resolution should my artwork be?",
        a: "All artwork must be a minimum of 300 DPI (dots per inch) at the final print size. Lower resolution files will result in blurry or pixelated prints."
      },
      {
        q: "Do I need to add bleed to my artwork?",
        a: "Yes. Please add 3mm bleed on all sides. Bleed ensures that when the print is trimmed, there are no white edges. Keep all important content (text, logos) at least 5mm from the trim edge."
      },
      {
        q: "What colour mode should I use?",
        a: "All files must be in CMYK colour mode, not RGB. RGB colours are used for screens and will look different when printed. Convert to CMYK in your design software before uploading."
      },
      {
        q: "Do you offer a free artwork check?",
        a: "Yes — every order includes a free artwork check. Our team reviews your file for resolution, bleed, colour mode, and any obvious errors before sending to print. We'll contact you if there are any issues."
      },
    ]
  },
  {
    category: "Turnaround & Delivery",
    items: [
      {
        q: "How fast can you print my order?",
        a: "We offer same-day dispatch on most standard products when ordered before 2pm. Express 24-hour and standard 3–5 day options are also available. Turnaround times are shown on each product page."
      },
      {
        q: "Do you deliver across the UK?",
        a: "Yes, we deliver to all UK mainland addresses. We use tracked courier services so you can follow your order from dispatch to delivery."
      },
      {
        q: "How much does delivery cost?",
        a: "Delivery costs depend on the size and weight of your order and your chosen turnaround. Free delivery is available on qualifying orders. Exact costs are shown at checkout."
      },
      {
        q: "Can I collect my order in person?",
        a: "Yes, collection is available from our facility. Select 'Collection' at checkout or contact us to arrange a collection time."
      },
    ]
  },
  {
    category: "Payment & Pricing",
    items: [
      {
        q: "What payment methods do you accept?",
        a: "We accept all major credit and debit cards (Visa, Mastercard, American Express) via our secure Stripe payment gateway. All transactions are SSL encrypted."
      },
      {
        q: "Are your prices inclusive of VAT?",
        a: "All prices shown are exclusive of VAT unless stated otherwise. VAT at the standard rate (20%) will be applied at checkout."
      },
      {
        q: "Do you offer trade or reseller pricing?",
        a: "Yes, we offer discounted trade pricing for print resellers, designers, and marketing agencies. Contact us to set up a trade account."
      },
    ]
  },
  {
    category: "Quality & Guarantee",
    items: [
      {
        q: "What is your satisfaction guarantee?",
        a: "We offer a 100% satisfaction guarantee. If your print doesn't meet the specification you ordered, we'll reprint or refund — no questions asked. Simply contact us within 7 days of receiving your order."
      },
      {
        q: "What if my order arrives damaged?",
        a: "In the unlikely event your order arrives damaged, please photograph the damage and contact us within 48 hours. We'll arrange a free reprint and collection of the damaged items."
      },
      {
        q: "Are you environmentally friendly?",
        a: "We are committed to sustainability. We offer FSC-certified paper stocks, use vegetable-based inks where possible, and our facility operates ISO 14001 environmental management standards."
      },
    ]
  },
];

function ChevronIcon({ open }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.25s ease", flexShrink: 0 }}>
      <polyline points="6 9 12 15 18 9"/>
    </svg>
  );
}

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={"faq-item" + (open ? " open" : "")} onClick={function() { setOpen(function(o) { return !o; }); }}>
      <div className="faq-question">
        <span>{q}</span>
        <ChevronIcon open={open} />
      </div>
      {open && <div className="faq-answer anim-fade-in-up">{a}</div>}
    </div>
  );
}

export default function FAQPage() {
  return (
    <div className="faq-page">
      {/* Hero */}
      <div className="faq-hero">
        <div className="container">
          <span className="tag">Help Centre</span>
          <h1>Frequently Asked Questions</h1>
          <p>Everything you need to know about ordering, artwork, delivery, and more.</p>
          <div className="faq-hero-cta">
            <span>Can't find your answer?</span>
            <Link to="/contact" className="btn-primary">Contact Us</Link>
          </div>
        </div>
      </div>

      {/* FAQ Content */}
      <div className="container faq-content">
        {FAQS.map(function(section) {
          return (
            <div key={section.category} className="faq-section">
              <h2 className="faq-category">{section.category}</h2>
              <div className="faq-list">
                {section.items.map(function(item) {
                  return <FAQItem key={item.q} q={item.q} a={item.a} />;
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Bottom CTA */}
      <div className="faq-bottom-cta">
        <div className="container faq-cta-inner reveal-zoom">
          <h3>Still have questions?</h3>
          <p>Our team is available Monday–Friday 8am–6pm and Saturday 9am–2pm.</p>
          <div className="faq-cta-btns">
            <Link to="/contact" className="btn-primary">Get in Touch</Link>
            <Link to="/products" className="btn-outline">Browse Products</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
